<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { searchContent, getSearchSuggestions, initializeSearchIndex } from '$lib/content/search';
  import type { SearchResult, SearchOptions } from '$lib/content/search';
  import type { ContentIndex, Locale } from '$lib/content/loader';
  
  interface Props {
    query: string;
    contentIndex: ContentIndex;
    locale: Locale;
    placeholder?: string;
    autofocus?: boolean;
    onResults?: (results: SearchResult[]) => void;
  }
  
  let { 
    query = $bindable(), 
    contentIndex, 
    locale, 
    placeholder = '', 
    autofocus = false,
    onResults
  }: Props = $props();
  
  let searchInput: HTMLInputElement;
  let isSearching = $state(false);
  let showSuggestions = $state(false);
  let suggestions = $state<string[]>([]);
  let searchResults = $state<SearchResult[]>([]);
  let selectedSuggestionIndex = $state(-1);
  
  // Search options
  let searchOptions = $state<SearchOptions>({
    types: [],
    limit: 20
  });
  
  onMount(() => {
    // Initialize search index
    initializeSearchIndex(contentIndex, locale);
    
    if (autofocus && searchInput) {
      searchInput.focus();
    }
    
    // Handle clicks outside to close suggestions
    const handleClickOutside = (event: MouseEvent) => {
      if (!searchInput.contains(event.target as Node)) {
        showSuggestions = false;
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });
  
  // Debounced search function
  let searchTimeout: number;
  function performSearch(searchQuery: string) {
    clearTimeout(searchTimeout);
    
    if (searchQuery.length < 2) {
      searchResults = [];
      suggestions = [];
      showSuggestions = false;
      onResults?.(searchResults);
      return;
    }
    
    isSearching = true;
    
    searchTimeout = setTimeout(() => {
      // Get search results
      searchResults = searchContent(searchQuery, locale, searchOptions);
      
      // Get suggestions
      suggestions = getSearchSuggestions(searchQuery, locale, 5);
      showSuggestions = suggestions.length > 0;
      
      isSearching = false;
      
      // Notify parent component
      onResults?.(searchResults);
      
      // Track search analytics
      if (typeof window !== 'undefined' && window.plausible) {
        window.plausible('Advanced Search', {
          props: { 
            query: searchQuery.substring(0, 50),
            resultsCount: searchResults.length,
            locale
          }
        });
      }
    }, 300);
  }
  
  function handleInput(event: Event) {
    const target = event.target as HTMLInputElement;
    query = target.value;
    selectedSuggestionIndex = -1;
    performSearch(query);
  }
  
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      query = '';
      showSuggestions = false;
      searchInput.blur();
      return;
    }
    
    if (!showSuggestions || suggestions.length === 0) return;
    
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      selectedSuggestionIndex = Math.min(selectedSuggestionIndex + 1, suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      selectedSuggestionIndex = Math.max(selectedSuggestionIndex - 1, -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (selectedSuggestionIndex >= 0) {
        selectSuggestion(suggestions[selectedSuggestionIndex]);
      }
    }
  }
  
  function selectSuggestion(suggestion: string) {
    query = suggestion;
    showSuggestions = false;
    selectedSuggestionIndex = -1;
    performSearch(query);
    searchInput.focus();
  }
  
  function clearSearch() {
    query = '';
    searchResults = [];
    suggestions = [];
    showSuggestions = false;
    onResults?.([]);
    searchInput.focus();
  }
  
  function handleFocus() {
    if (query.length >= 2 && suggestions.length > 0) {
      showSuggestions = true;
    }
  }
</script>

<div class="advanced-search-box">
  <div class="search-box__wrapper">
    <div class="search-box__icon">
      {#if isSearching}
        <div class="search-spinner">⏳</div>
      {:else}
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
        </svg>
      {/if}
    </div>
    
    <input
      bind:this={searchInput}
      type="text"
      class="search-box__input"
      {placeholder}
      value={query}
      oninput={handleInput}
      onkeydown={handleKeydown}
      onfocus={handleFocus}
      autocomplete="off"
      spellcheck="false"
      role="combobox"
      aria-expanded={showSuggestions}
      aria-haspopup="listbox"
      aria-autocomplete="list"
      aria-controls="search-suggestions"
    />
    
    {#if query}
      <button
        class="search-box__clear"
        onclick={clearSearch}
        aria-label={$_('search.clear')}
        title={$_('search.clear')}
      >
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    {/if}
    
    <!-- Search Suggestions Dropdown -->
    {#if showSuggestions && suggestions.length > 0}
      <div class="search-suggestions" role="listbox" id="search-suggestions">
        {#each suggestions as suggestion, index}
          <button
            class="search-suggestion {selectedSuggestionIndex === index ? 'search-suggestion--selected' : ''}"
            onclick={() => selectSuggestion(suggestion)}
            role="option"
            aria-selected={selectedSuggestionIndex === index}
          >
            <span class="search-suggestion__icon">🔍</span>
            <span class="search-suggestion__text">{suggestion}</span>
          </button>
        {/each}
      </div>
    {/if}
  </div>
  
  <!-- Search Status -->
  {#if query && query.length > 0}
    <div class="search-status">
      {#if query.length < 2}
        <span class="search-status__hint">
          {$_('search.minLength', { values: { min: 2 } })}
        </span>
      {:else if isSearching}
        <span class="search-status__searching">
          {$_('search.searching')}...
        </span>
      {:else if searchResults.length > 0}
        <span class="search-status__results">
          {$_('search.results.found', { values: { count: searchResults.length } })}
        </span>
      {:else}
        <span class="search-status__no-results">
          {$_('search.noResults.short')}
        </span>
      {/if}
    </div>
  {/if}
</div>

<style>
  .advanced-search-box {
    position: relative;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  }
  
  .search-box__wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }
  
  .search-box__icon {
    position: absolute;
    left: var(--space-3);
    width: 20px;
    height: 20px;
    color: var(--text-tertiary);
    pointer-events: none;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .search-spinner {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .search-box__input {
    width: 100%;
    padding: var(--space-4) var(--space-12) var(--space-4) var(--space-10);
    border: 2px solid var(--border-light);
    border-radius: var(--radius-lg);
    background: var(--bg-primary);
    color: var(--text-primary);
    font-size: var(--font-size-base);
    line-height: 1.5;
    transition: var(--transition-all);
  }
  
  .search-box__input:focus {
    outline: none;
    border-color: var(--brand-primary);
    box-shadow: 0 0 0 3px var(--brand-primary-light);
  }
  
  .search-box__input::placeholder {
    color: var(--text-tertiary);
  }
  
  .search-box__clear {
    position: absolute;
    right: var(--space-3);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    border-radius: var(--radius-sm);
    transition: var(--transition-colors);
  }
  
  .search-box__clear:hover {
    color: var(--text-primary);
    background: var(--bg-secondary);
  }
  
  .search-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-top: none;
    border-radius: 0 0 var(--radius-lg) var(--radius-lg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 200px;
    overflow-y: auto;
  }
  
  .search-suggestion {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    padding: var(--space-3) var(--space-4);
    border: none;
    background: none;
    color: var(--text-primary);
    font-size: var(--font-size-sm);
    text-align: left;
    cursor: pointer;
    transition: var(--transition-colors);
  }
  
  .search-suggestion:hover,
  .search-suggestion--selected {
    background: var(--bg-secondary);
  }
  
  .search-suggestion__icon {
    opacity: 0.6;
    font-size: var(--font-size-xs);
  }
  
  .search-suggestion__text {
    flex: 1;
  }
  
  .search-status {
    margin-top: var(--space-2);
    text-align: center;
    font-size: var(--font-size-sm);
  }
  
  .search-status__hint {
    color: var(--text-tertiary);
  }
  
  .search-status__searching {
    color: var(--brand-primary);
    font-weight: var(--font-weight-medium);
  }
  
  .search-status__results {
    color: var(--text-secondary);
  }
  
  .search-status__no-results {
    color: var(--text-tertiary);
  }
  
  @media (max-width: 768px) {
    .search-box__input {
      padding: var(--space-3) var(--space-10) var(--space-3) var(--space-9);
      font-size: var(--font-size-base);
    }
    
    .search-box__icon {
      left: var(--space-2);
      width: 18px;
      height: 18px;
    }
    
    .search-box__clear {
      right: var(--space-2);
      width: 20px;
      height: 20px;
    }
    
    .search-suggestion {
      padding: var(--space-2) var(--space-3);
    }
  }
</style>
